/**
 * i18next configuration for FishMemory app.
 *
 * All 111 languages are bundled via lazy loaders. Core languages (en, ar, lb, fr)
 * are loaded eagerly at init. Other languages are loaded on demand when the user
 * switches language via changeLanguage(). English is always the fallback.
 *
 * RTL layout determined dynamically from language registry.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DevSettings, I18nManager } from 'react-native';
import * as storage from '../utils/storage';
import { isRtlLang } from '../utils/constants';

import en from './en.json';
import ar from './ar.json';
import lb from './lb.json';
import fr from './fr.json';

/**
 * Lazy language loaders — Metro bundler resolves require() at build time.
 * Each returns the JSON translation object. Only called when the language
 * is first selected, then cached by i18next via addResourceBundle.
 */
const languageLoaders: Record<string, () => Record<string, unknown>> = {
  en: () => en,
  ar: () => ar,
  lb: () => lb,
  fr: () => fr,
  // Launch languages
  zh: () => require('./zh.json'),
  hi: () => require('./hi.json'),
  es: () => require('./es.json'),
  bn: () => require('./bn.json'),
  pt: () => require('./pt.json'),
  ru: () => require('./ru.json'),
  ja: () => require('./ja.json'),
  // Community languages
  af: () => require('./af.json'),
  am: () => require('./am.json'),
  as: () => require('./as.json'),
  az: () => require('./az.json'),
  be: () => require('./be.json'),
  bg: () => require('./bg.json'),
  bs: () => require('./bs.json'),
  ca: () => require('./ca.json'),
  cs: () => require('./cs.json'),
  cy: () => require('./cy.json'),
  da: () => require('./da.json'),
  de: () => require('./de.json'),
  dv: () => require('./dv.json'),
  el: () => require('./el.json'),
  eo: () => require('./eo.json'),
  et: () => require('./et.json'),
  eu: () => require('./eu.json'),
  fa: () => require('./fa.json'),
  fi: () => require('./fi.json'),
  fy: () => require('./fy.json'),
  ga: () => require('./ga.json'),
  gd: () => require('./gd.json'),
  gl: () => require('./gl.json'),
  gu: () => require('./gu.json'),
  ha: () => require('./ha.json'),
  he: () => require('./he.json'),
  hr: () => require('./hr.json'),
  ht: () => require('./ht.json'),
  hu: () => require('./hu.json'),
  hy: () => require('./hy.json'),
  id: () => require('./id.json'),
  ig: () => require('./ig.json'),
  is: () => require('./is.json'),
  it: () => require('./it.json'),
  jv: () => require('./jv.json'),
  ka: () => require('./ka.json'),
  kk: () => require('./kk.json'),
  km: () => require('./km.json'),
  kn: () => require('./kn.json'),
  ko: () => require('./ko.json'),
  ku: () => require('./ku.json'),
  ky: () => require('./ky.json'),
  la: () => require('./la.json'),
  ln: () => require('./ln.json'),
  lo: () => require('./lo.json'),
  lt: () => require('./lt.json'),
  lv: () => require('./lv.json'),
  mg: () => require('./mg.json'),
  mi: () => require('./mi.json'),
  mk: () => require('./mk.json'),
  ml: () => require('./ml.json'),
  mn: () => require('./mn.json'),
  mr: () => require('./mr.json'),
  ms: () => require('./ms.json'),
  mt: () => require('./mt.json'),
  my: () => require('./my.json'),
  ne: () => require('./ne.json'),
  nl: () => require('./nl.json'),
  no: () => require('./no.json'),
  ny: () => require('./ny.json'),
  or: () => require('./or.json'),
  pa: () => require('./pa.json'),
  pl: () => require('./pl.json'),
  ps: () => require('./ps.json'),
  ro: () => require('./ro.json'),
  rw: () => require('./rw.json'),
  sd: () => require('./sd.json'),
  si: () => require('./si.json'),
  sk: () => require('./sk.json'),
  sl: () => require('./sl.json'),
  sm: () => require('./sm.json'),
  sn: () => require('./sn.json'),
  so: () => require('./so.json'),
  sq: () => require('./sq.json'),
  sr: () => require('./sr.json'),
  st: () => require('./st.json'),
  su: () => require('./su.json'),
  sv: () => require('./sv.json'),
  sw: () => require('./sw.json'),
  ta: () => require('./ta.json'),
  te: () => require('./te.json'),
  tg: () => require('./tg.json'),
  th: () => require('./th.json'),
  ti: () => require('./ti.json'),
  tk: () => require('./tk.json'),
  tl: () => require('./tl.json'),
  tn: () => require('./tn.json'),
  to: () => require('./to.json'),
  tr: () => require('./tr.json'),
  tt: () => require('./tt.json'),
  ug: () => require('./ug.json'),
  uk: () => require('./uk.json'),
  ur: () => require('./ur.json'),
  uz: () => require('./uz.json'),
  vi: () => require('./vi.json'),
  wo: () => require('./wo.json'),
  xh: () => require('./xh.json'),
  yi: () => require('./yi.json'),
  yo: () => require('./yo.json'),
  zu: () => require('./zu.json'),
};

// Core languages loaded eagerly at init
const resources: Record<string, { translation: Record<string, unknown> }> = {
  en: { translation: en },
  ar: { translation: ar },
  lb: { translation: lb },
  fr: { translation: fr },
};

i18n
  .use(initReactI18next)
  .init({
    compatibilityJSON: 'v4',
    resources,
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

/**
 * Ensure a language's resource bundle is loaded into i18next.
 * Called automatically by changeLanguage before switching.
 */
function ensureLanguageLoaded(lang: string): void {
  if (i18n.hasResourceBundle(lang, 'translation')) return;
  const loader = languageLoaders[lang];
  if (loader) {
    i18n.addResourceBundle(lang, 'translation', loader(), true, true);
  }
  // If no loader exists, i18next will fall back to English
}

/**
 * Load persisted language from storage and apply it.
 */
export async function loadPersistedLanguage(): Promise<void> {
  try {
    const lang = await storage.getLanguage();
    const shouldUseRTL = isRtlLang(lang || 'en');
    if (lang && (lang !== i18n.language || I18nManager.isRTL !== shouldUseRTL)) {
      await changeLanguage(lang);
    }
  } catch {
    // Ignore — default language is fine
  }
}

async function reloadAppForLayoutDirection(): Promise<void> {
  const expoReload = (globalThis as typeof globalThis & {
    expo?: { reloadAppAsync?: (reason?: string) => Promise<void> };
  }).expo?.reloadAppAsync;

  if (expoReload) {
    await expoReload('Language direction changed');
    return;
  }

  if (typeof DevSettings.reload === 'function') {
    DevSettings.reload();
  }
}

/**
 * Change language, load resource bundle on demand, handle RTL, and persist.
 */
export async function changeLanguage(lang: string): Promise<void> {
  const isRTL = isRtlLang(lang);
  const directionChanged = I18nManager.isRTL !== isRTL;

  ensureLanguageLoaded(lang);
  await i18n.changeLanguage(lang);
  await storage.setLanguage(lang).catch(() => {});

  if (directionChanged) {
    I18nManager.allowRTL(isRTL);
    I18nManager.forceRTL(isRTL);
    I18nManager.swapLeftAndRightInRTL(isRTL);
    await reloadAppForLayoutDirection();
  }
}

export default i18n;
